import moment from "moment";
import { capitalizeOnlyFirstLetter } from "./utils";

// Define types for the structure of the data
interface Pallet {
  palletName: string;
  itemName: string;
  modelName: string;
  palletType: string;
  containerType: string;
  containerTypeName?: string;
  quantity: number;
  totalQuantity: number;
  remainingItems: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
  weight: number | null;
  quantityPerBox?: number;
  palletDimensions: {
    palletLength?: number;
    palletWidth?: number;
    palletHeight?: number;
    palletWeight?: number;
  };
}

interface SalesOrder {
  shippingAddress: string;
  shippingMethod: string | null;
  refNum?: string;
}

interface DeliveryOrderData {
  customerName: string;
  customerAddress?: string | any;
  pallets: Pallet[];
  salesOrders: SalesOrder[];
  bondedDetails?: string;
  date?: string;
}

// Helper function to calculate vertical margin for centering content in rowspan cells
const calculateVerticalMargin = (rowSpanCount: number): number => {
  if (rowSpanCount === 1) return 0;
  if (rowSpanCount === 2) return 6;
  if (rowSpanCount === 3) return 12;
  return (rowSpanCount - 1) * 6;
};

export const generateDeliveryOrderHTML = (
  data: DeliveryOrderData,
  logoBase64?: string
): string => {
  const currentDate = data.date
    ? moment(data.date).format("MM/DD/YYYY")
    : moment(new Date()).format("MM/DD/YYYY");
  const logoDataUri = `${process.env.REACT_APP_LOGO || ""}`;

  // Get customer address from API data
  let customerAddress = null;
  if (data.customerAddress) {
    if (typeof data.customerAddress === "string") {
      try {
        customerAddress = JSON.parse(data.customerAddress);
      } catch (e) {
        customerAddress = null;
      }
    } else {
      customerAddress = data.customerAddress;
    }
  }

  // Group pallets by palletName
  const groupedPallets = data.pallets.reduce((groups, pallet) => {
    const palletName = pallet.palletName;
    if (!groups[palletName]) {
      groups[palletName] = [];
    }
    groups[palletName].push(pallet);
    return groups;
  }, {} as Record<string, Pallet[]>);

  const salesOrderNumbers = data.salesOrders
    .map((order: any) => order?.refNum)
    .filter((refNum) => refNum)
    .join(", ");

  // Calculate total weight and total boxes
  let totalWeight = 0;
  let totalBoxes = 0;

  // Add pallet weights to total weight
  Object.entries(groupedPallets).forEach(([palletName, pallets]) => {
    const firstPallet = pallets[0];
    const dimensions = firstPallet.palletDimensions || {};
    const palletWeight = parseFloat(String(dimensions.palletWeight ?? "0"));
    totalWeight += palletWeight;

    let palletBoxCount = 1;

    // Create a map to track box groups
    const boxGroupMap: Record<string, any[]> = {};

    // First pass: identify box groups while maintaining order
    pallets.forEach((pallet: any, idx) => {
      const type = pallet.containerTypeName || pallet.containerType;
      const normalizedType = type ? type.trim().toLowerCase() : "";

      if (normalizedType && normalizedType.startsWith("box")) {
        const key = `${type}_${pallet.length}_${pallet.width}_${pallet.height}`;
        if (!boxGroupMap[key]) {
          boxGroupMap[key] = [];
        }
        boxGroupMap[key].push({ ...pallet, originalIndex: idx });
      }
    });

    // Second pass: process pallets in original order to count boxes
    pallets.forEach((pallet: any, idx) => {
      const type = pallet.containerTypeName || pallet.containerType;
      const normalizedType = type ? type.trim().toLowerCase() : "";

      if (normalizedType && normalizedType.startsWith("box")) {
        const key = `${type}_${pallet.length}_${pallet.width}_${pallet.height}`;
        const group = boxGroupMap[key];

        // Only process if this is the first occurrence of this group
        if (group[0].originalIndex === idx) {
          group.forEach((groupPallet, groupIdx) => {
            if (
              groupIdx != 0 &&
              groupPallet.containerType === "BOX" &&
              group[groupIdx === 0 ? 0 : groupIdx - 1].containerType === "BOX"
            ) {
              // Skip incrementing - keep palletBoxCount as is
            } else {
              palletBoxCount++;
            }
          });
        }
      } else {
        // Handle non-box items
        palletBoxCount += pallet.quantity || 0;
      }
    });

    totalBoxes += palletBoxCount - 1;
  });

  return `
  <!DOCTYPE html>
  <html lang="en">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
      html, body {
        min-height: 100vh;
        margin: 0;
        padding: 0;
      }
      body {
        font-family: Arial, sans-serif;
        color: #333;
        background-color: #fff;
        display: flex;
        flex-direction: column;
      }
      .container {
        max-width: 1000px;
        margin: 20px auto;
        background-color: #fff;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
      }
      .content-wrapper {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height:100%;
      }
      .main-content {
        flex: 1 0 auto;
      }
      .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
      }
      .header-left {
        display: flex;
        align-items: center;
      }
      .header-left img {
        max-width: 150px;
      }
      .header-right {
        text-align: right;
      }
      .header-right h1 {
        font-size: 18px;
        color: #333;
        font-weight: 600;
        margin: 0;
      }
      .header-row-2 {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
      }
      .shipper-section {
        flex: 1;
        min-width: 300px;
      }
      .shipper-section p {
        margin: 5px 0;
        line-height: 1.6;
        font-size: 12px;
      }
      .bonded-section {
        font-size: 12px;
        margin-left: auto;
        text-align: right;
      }
      .bonded-section p {
        margin: 5px 5px;
        font-size: 12px;
        color: #333;
        font-weight: 500;
        line-height: 1.6;
        max-width: 180px;
        word-wrap: break-word;
        overflow-wrap: break-word;
        text-align: right;
        margin-left: auto;
      }
      .details {
        margin-bottom: 10px;
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
      }
      .details p {

        margin: 5px 0;
        line-height: 1.6;
        font-size: 12px;
      }
      .details .section {
        flex: 1;
        min-width: 300px;
      }
      .table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 15px;
        border-radius: 8px;
        table-layout: auto;
      }
      .table th, .table td {
        border: 1px solid #ddd;
        padding: 8px 10px;
        text-align: left;
        font-size: 12px;
        vertical-align: top;
        word-wrap: normal;
        overflow: visible;
      }
      .table th {
        background-color: #f1f1f1;
        font-weight: bold;
        color: #333;
        white-space: nowrap;
        overflow: visible;
        text-overflow: clip;
      }
     
      /* Column width settings */
      .table th:nth-child(1),
      .table td:nth-child(1) {
        width: 8%;  /* Box Range */
        min-width: 60px;
      }
      .table th:nth-child(2),
      .table td:nth-child(2) {
        width: 14%; /* Item Name */
        min-width: 100px;
      }
      .table th:nth-child(3),
      .table td:nth-child(3) {
        width: 11%; /* Container Type */
        min-width: 90px;
      }
      .table th:nth-child(4),
      .table td:nth-child(4) {
        width: 11%;  /* Quantity */
        min-width: 80px;
      }
      .table th:nth-child(5),
      .table td:nth-child(5) {
        width: 11%; /* Qty Per Box */
        min-width: 85px;
      }
      .table th:nth-child(6),
      .table td:nth-child(6) {
        width: 13%; /* Total Quantity */
        min-width: 95px;
      }
      .table th:nth-child(7),
      .table td:nth-child(7) {
        width: 13%; /* Box Dimensions */
        min-width: 100px;
      }
      .table th:nth-child(8),
      .table td:nth-child(8) {
        width: 11%; /* Box Weight */
        min-width: 85px;
      }

      /* Vertical centering for rowspan cells */
      .v-center-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
      }

      .pallet {
        margin-top: 1px;
        margin-bottom: 1px;
        background-color: transparent!important;
      }
      .pallet-title {
        font-weight: 400;
        margin-bottom: 12px;
        color: #286fd1;
        font-size: 14px;
      }
      .section1 {
        font-size: 12px;
      }
      .section2 {
        font-size: 12px;
        margin-left: auto;
      }
      .footer {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        font-size: 14px;
        color: #666;
        margin-top: 60px;
        margin-bottom: 40px;
      }
      .footer-left, .footer-right {
        flex: 1;
      }
      .footer-left p, .footer-right p {
        line-height: 1.8;
      }
      .signature-section {
        margin-top: auto;
       padding-top: 30px;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        font-size: 12px;
        color: #333;
        padding: 15px 0;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .signature-left, .signature-right {
        flex: 1;
      }
      .signature-left p, .signature-right p {
        margin: 8px 0;
        line-height: 1.8;
        font-size: 12px;
      }
      .signature-left strong, .signature-right strong {
        font-weight: 600;
      }
      .signature-line {
        border-bottom: 1px solid #333;
        min-width: 200px;
        display: inline-block;
        margin-left: 10px;
      }
      .footer .totals {
        display: flex;
        flex-direction: column;
        gap: 5px;
        font-weight: bold;
        color: #333;
      }
      .merged-row {
        border-top: 1px solid #ddd;
      }
      .no-border-row {
        border-top: none;
      }
      .merged-rows-group {
        background-color: #fff;
      }
      .box-range {
        white-space: nowrap;
        text-align: center;
      }
      .center-align {
        text-align: center;
      }
      .right-align {
        text-align: right;
      }
      .table tbody tr:hover {
        background-color: transparent;
      }
    </style>
  </head>
  <body>
    <div class="container">
      <div class="content-wrapper">
        <div class="main-content">
          <div class="header">
            <div class="header-left">
              <img src="${logoDataUri}" alt="Logo" />
            </div>
            <div class="header-right">
              <h1>DELIVERY ORDER</h1>
            </div>
          </div>
          <div class="header-row-2">
            <div class="shipper-section">
              <p><strong>Shipper/Pickup:</strong></p>
              <p>3nStar Inc.</p>
              <p>10813 NW 30th Street, Suite #110</p>
              <p>Doral, FL, 33172</p>
            </div>
            <div class="bonded-section">
              <p><strong>Bonded:</strong> ${data.bondedDetails || ""}</p>
            </div>
          </div>
          <div class="details">
            <div class="section1">
              <p><strong>Date:</strong> ${currentDate || "-"}</p>
              <p><strong>Sales Order No:</strong> ${salesOrderNumbers || "-"}</p>
              <p><strong>Customer Name:</strong> ${data.customerName || "-"}</p>
              ${
                customerAddress
                  ? `
              <p style="margin-top: 8px;"><strong>Customer Address:</strong><br>
                ${customerAddress.addr1 || ""}${
                      customerAddress.addr2 ? "<br>" + customerAddress.addr2 : ""
                    }${
                      customerAddress.addr3 ? "<br>" + customerAddress.addr3 : ""
                    }${
                      customerAddress.addr4 ? "<br>" + customerAddress.addr4 : ""
                    }${customerAddress.city ? "<br>" + customerAddress.city : ""}${
                      customerAddress.state ? ", " + customerAddress.state : ""
                    }${
                      customerAddress.postalCode
                        ? " " + customerAddress.postalCode
                        : ""
                    }${
                      customerAddress.country
                        ? "<br>" + customerAddress.country
                        : ""
                    }
              </p>
              `
                  : ""
              }
            </div>
            <div class="section2">
              <p><strong>Ship To:</strong> ${
                data.salesOrders[0]?.shippingAddress &&
                data.salesOrders[0]?.shippingAddress !== "{}"
                  ? (() => {
                      try {
                        const address = JSON.parse(
                          data.salesOrders[0].shippingAddress
                        );
                        const addressParts = [
                          address.addr1 || "",
                          address.addr2 || "",
                          address.city || "",
                          address.state || "",
                          address.country || "",
                        ].filter((part) => part);

                        return addressParts.join("<br>") || "-";
                      } catch (e) {
                        return data.salesOrders[0].shippingAddress;
                      }
                    })()
                  : "N/A"
              }</p>

              <p><strong>Ship Via:</strong> ${
                data.salesOrders[0]?.shippingMethod &&
                data.salesOrders[0]?.shippingMethod !== "{}"
                  ? (() => {
                      try {
                        const method = JSON.parse(
                          data.salesOrders[0].shippingMethod
                        );
                        return method.FullName || "N/A";
                      } catch (e) {
                        return "-";
                      }
                    })()
                  : "N/A"
              }</p>
            </div>
          </div>
          ${Object.entries(groupedPallets)
            .map(([palletName, pallets]: [string, any[]], palletIndex) => {
              const firstPallet = pallets[0];
              const dimensions = firstPallet.palletDimensions || {};
              const length = dimensions.palletLength || "0";
              const width = dimensions.palletWidth || "0";
              const height = dimensions.palletHeight || "0";
              const weight = dimensions.palletWeight || "0";

              const isMixPallet =
                firstPallet.palletType &&
                firstPallet.palletType.toUpperCase() === "MIXPALLET";

              const palletSequence = palletIndex + 1;
              const palletTitleText = isMixPallet
                ? `Pallet ${palletSequence} (Mix)`
                : `Pallet ${palletSequence}`;

              let tableRows = "";
              let boxIndex = 1;

              // Create a map to track box groups
              const boxGroupMap: Record<string, any[]> = {};

              // First pass: identify box groups while maintaining order
              pallets.forEach((pallet, idx) => {
                const type = pallet.containerTypeName || pallet.containerType;
                const normalizedType = type ? type.trim().toLowerCase() : "";

                if (normalizedType && normalizedType.startsWith("box")) {
                  const key = `${type}_${pallet.length}_${pallet.width}_${pallet.height}`;
                  if (!boxGroupMap[key]) {
                    boxGroupMap[key] = [];
                  }
                  boxGroupMap[key].push({ ...pallet, originalIndex: idx });
                }
              });

              // Second pass: process pallets in original order
              pallets.forEach((pallet, idx) => {
                const type = pallet.containerTypeName || pallet.containerType;
                const normalizedType = type ? type.trim().toLowerCase() : "";

                if (normalizedType && normalizedType.startsWith("box")) {
                  const key = `${type}_${pallet.length}_${pallet.width}_${pallet.height}`;
                  const group = boxGroupMap[key];

                  // Only process if this is the first occurrence of this group
                  if (group[0].originalIndex === idx) {
                    const verticalMargin = calculateVerticalMargin(group.length);
                    group.forEach((groupPallet, groupIdx) => {
                      if (groupIdx === 0) {
                        tableRows += `
                        <tr>
                          <td class="box-range" rowspan="${
                            group.length
                          }"><div style="margin-top: ${verticalMargin}px;">${boxIndex}-${boxIndex}</div></td>
                          <td>${groupPallet.itemName || "-"}</td>
                          <td rowspan="${group.length}"><div style="margin-top: ${verticalMargin}px;">${
                          capitalizeOnlyFirstLetter(
                            groupPallet.containerTypeName ||
                              groupPallet.containerType
                          ) || "-"
                        }</div></td>
                          <td class="center-align">${
                            groupPallet.quantity || "-"
                          }</td>
                          <td class="center-align">${
                            groupPallet.quantityPerBox || "-"
                          }</td>
                          <td class="center-align">${
                            groupPallet.totalQuantity || "0"
                          }</td>
                          <td rowspan="${group.length}"><div style="margin-top: ${verticalMargin}px;">${groupPallet.length || "0"}x${
                          groupPallet.width || "0"
                        }x${groupPallet.height || "0"}</div></td>
                          <td rowspan="${group.length}" class="right-align"><div style="margin-top: ${verticalMargin}px;">${
                          groupPallet.weight ? groupPallet.weight.toFixed(2) : "-"
                        }</div></td>
                        </tr>`;
                      } else {
                        tableRows += `
                        <tr>
                          <td>${groupPallet.itemName || "-"}</td>
                          <td class="center-align">${
                            groupPallet.quantity || "-"
                          }</td>
                          <td class="center-align">${
                            groupPallet.quantityPerBox || "-"
                          }</td>
                          <td class="center-align">${
                            groupPallet.totalQuantity || "0"
                          }</td>
                        </tr>`;
                      }
                      if (
                        groupIdx != 0 &&
                        groupPallet.containerType === "BOX" &&
                        group[groupIdx === 0 ? 0 : groupIdx - 1].containerType ===
                          "BOX"
                      ) {
                        // Skip incrementing for consecutive BOX items - don't increment boxIndex
                      } else {
                        boxIndex++;
                      }
                    });
                  }
                } else {
                  // Handle non-box items
                  const start = boxIndex;
                  const end = start + (pallet.quantity || 0) - 1;
                  tableRows += `
                  <tr>
                    <td class="box-range">${start}-${end}</td>
                    <td>${pallet.itemName || "-"}</td>
                    <td>${
                      capitalizeOnlyFirstLetter(
                        pallet.containerTypeName || pallet.containerType
                      ) || "-"
                    }</td>
                    <td class="center-align">${pallet.quantity || "-"}</td>
                    <td class="center-align">${pallet.quantityPerBox || "-"}</td>
                    <td class="center-align">${pallet.totalQuantity || "0"}</td>
                    <td>${pallet.length || "0"}x${pallet.width || "0"}x${pallet.height || "0"}</td>
                    <td class="right-align">${
                      pallet.weight ? pallet.weight.toFixed(2) : "-"
                    }</td>
                  </tr>`;

                  boxIndex += pallet.quantity || 0;
                }
              });

              const totalBoxes = boxIndex - 1;

              return `
                <div class="pallet">
                  <p class="pallet-title">${palletTitleText} - Dimensions: ${length}x${width}x${height}, Weight: ${weight}, Total Boxes: ${totalBoxes}</p>
                  <table class="table">
                    <thead>
                      <tr>
                        <th>Box Range</th>
                        <th>Item</th>
                        <th>Container Type</th>
                        <th>Quantity</th>
                        <th>Qty Per Box</th>
                        <th>Total Quantity</th>
                        <th>Box Dimensions</th>
                        <th>Box Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${tableRows}
                    </tbody>
                  </table>
                </div>`;
            })
            .join("")}
          <div class="footer">
            <div class="totals">
              <span>Total Weight: ${totalWeight.toFixed(2)}</span>
              <span>Total Boxes: ${totalBoxes}</span>
            </div>
            <p>Packaging Manager</p>
          </div>
        </div>
        <div class="signature-section">
          <div class="signature-left">
            <p><strong>Driver (Print Name):</strong> <span class="signature-line">&nbsp;</span></p>
            <p><strong>Date:</strong> <span class="signature-line">&nbsp;</span></p>
            <p><strong>Time:</strong> <span class="signature-line">&nbsp;</span></p>
          </div>
          <div class="signature-right">
            <p><strong>Received By (Print Name):</strong> <span class="signature-line">&nbsp;</span></p>
            <p><strong>Date:</strong> <span class="signature-line">&nbsp;</span></p>
            <p><strong>Time:</strong> <span class="signature-line">&nbsp;</span></p>
          </div>
        </div>
      </div>
    </div>
  </body>
  </html>
  `;
};